import {
  createContext,
  PropsWithChildren,
  use,
  useEffect,
  useMemo,
} from "react";
import { useDialog } from "~/hooks/use-dialog";
import { useAuth, useSession } from "~/contexts/auth-context";
import { KeywordsPipelineInfoModal } from "~/components/keywords-tab/components/unified-keywords-table/keywords-pipeline-info-modal";

interface KeywordsPipelineInfoDialogContextType {
  isOpen: boolean;
  open: () => void;
}

const KeywordsPipelineInfoDialogContext =
  createContext<KeywordsPipelineInfoDialogContextType | null>(null);

export const KeywordsPipelineInfoDialogProvider = ({
  children,
  autoOpen = true,
}: PropsWithChildren<{ autoOpen?: boolean }>) => {
  "use no memo";

  const { user } = useSession();
  const { updateUserSettings } = useAuth();
  const keywordsPipelineInfoDialog = useDialog("keywords-pipeline-info");

  const hasSeenPipelineInfo = user.settings?.keywordsPipelineInfoSeen;

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-running on isOpen would reopen the dialog as soon as it closes
  useEffect(() => {
    if (
      autoOpen &&
      !hasSeenPipelineInfo &&
      !keywordsPipelineInfoDialog.isOpen
    ) {
      keywordsPipelineInfoDialog.open();
    }
  }, [autoOpen, hasSeenPipelineInfo]);

  const handleModalClose = async (open: boolean) => {
    keywordsPipelineInfoDialog.onOpenChange(open);
    // Only the auto-open surface (ProductDetails) persists "seen". The manuscript
    // page opens this modal on demand (autoOpen=false) and intentionally does not
    // mark it seen, so a manual view there doesn't suppress the one-time auto-open
    // education on ProductDetails.
    if (autoOpen && !open && !hasSeenPipelineInfo) {
      await updateUserSettings({ keywordsPipelineInfoSeen: true });
    }
  };

  const value = useMemo(
    () => ({
      isOpen: keywordsPipelineInfoDialog.isOpen,
      open: keywordsPipelineInfoDialog.open,
    }),
    [keywordsPipelineInfoDialog.isOpen, keywordsPipelineInfoDialog.open],
  );

  return (
    <KeywordsPipelineInfoDialogContext value={value}>
      {children}
      <KeywordsPipelineInfoModal
        dialog={{
          ...keywordsPipelineInfoDialog,
          onOpenChange: handleModalClose,
        }}
      />
    </KeywordsPipelineInfoDialogContext>
  );
};

export const useKeywordsPipelineInfoDialog = () => {
  const context = use(KeywordsPipelineInfoDialogContext);
  if (!context) {
    throw new Error(
      "useKeywordsPipelineInfoDialog must be used within KeywordsPipelineInfoDialogProvider",
    );
  }
  return context;
};
